import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, SlidersHorizontal, ClipboardCheck, TrendingUp } from 'lucide-react';
import { Driver, Forecast, forecastsApi } from '@/lib/forecasts';
import { AppShell } from '@/components/app/AppShell';
import { ConfirmModal } from '@/components/ConfirmModal';
import { WhatIfPanel } from '@/components/app/WhatIfPanel';

type TFn = (k: string, opts?: Record<string, unknown>) => string;

const BASES: Driver['basis'][] = ['history_avg', 'history_trend', 'user_input'];
const blankDriver = (): Driver => ({ name: '', basis: 'user_input', value: 0, rationale: '' });

export function ForecastPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: forecasts = [], isLoading } = useQuery({
    queryKey: ['forecasts', wsId],
    queryFn: () => forecastsApi.list(wsId!),
    enabled: !!wsId,
  });

  // Compare mode is driven by the ?compare=id1,id2 URL param so the page
  // is shareable via a copied link. Empty = list mode.
  const compareParam = searchParams.get('compare') ?? '';
  const compareIds = useMemo(
    () => compareParam.split(',').map((s) => s.trim()).filter(Boolean),
    [compareParam],
  );
  const compareMode = compareIds.length > 0;
  const selectedForecasts = compareMode
    ? compareIds
        .map((id) => forecasts.find((f) => f.id === id))
        .filter((f): f is Forecast => Boolean(f))
    : [];

  const [selecting, setSelecting] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  function toggleSelect(id: string) {
    setSelecting((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function applyCompare() {
    if (selecting.size < 2) return;
    setSearchParams({ compare: Array.from(selecting).join(',') });
    setSelecting(new Set());
  }
  function exitCompare() {
    searchParams.delete('compare');
    setSearchParams(searchParams);
  }
  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied — silently skip */
    }
  }

  const [proposeState, setProposeState] = useState<{ open: boolean; periodStart: string; periodEnd: string; contextNote: string }>({
    open: false,
    periodStart: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().slice(0, 10),
    periodEnd: new Date(new Date().getFullYear() - 1, 11, 31).toISOString().slice(0, 10),
    contextNote: '',
  });

  const [blank, setBlank] = useState<{ open: boolean; label: string; drivers: Driver[] }>({
    open: false,
    label: '',
    drivers: [blankDriver()],
  });

  const propose = useMutation({
    mutationFn: () => forecastsApi.proposeDrivers(wsId!, {
      periodStart: proposeState.periodStart,
      periodEnd: proposeState.periodEnd,
      contextNote: proposeState.contextNote || undefined,
    }),
  });

  const create = useMutation({
    mutationFn: (body: { scenarioLabel: string; drivers: Driver[]; aiSuggestions?: Driver[] }) =>
      forecastsApi.create(wsId!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forecasts', wsId] });
      propose.reset();
      setBlank({ open: false, label: '', drivers: [blankDriver()] });
    },
  });

  return (
    <AppShell title={t('forecast.title')} subtitle={t('forecast.subtitle')}>
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('forecast.title')}</h1>
            <p className="mt-1 text-ink-600">{t('forecast.subtitle')}</p>
          </div>
          {!compareMode && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setBlank((s) => ({ ...s, open: !s.open }))}
                className="px-4 py-2 rounded-lg border border-border text-ink-700 text-sm font-semibold hover:bg-foreground/[0.04] transition inline-flex items-center gap-1.5"
              >
                <Plus className="h-4 w-4" /> {t('forecast.new_blank')}
              </button>
              <button
                onClick={() => setProposeState((s) => ({ ...s, open: true }))}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition"
              >
                {t('forecast.ask_ai')}
              </button>
            </div>
          )}
          {compareMode && (
            <div className="flex items-center gap-2">
              <button
                onClick={copyShareLink}
                className="px-3 py-2 rounded-lg border border-border text-ink-700 hover:bg-foreground/[0.04] text-xs font-semibold"
              >
                {copied ? t('forecast.link_copied') : t('forecast.compare_copy_link')}
              </button>
              <button
                onClick={exitCompare}
                className="px-3 py-2 rounded-lg border border-border text-ink-700 hover:bg-foreground/[0.04] text-xs"
              >
                {t('forecast.exit_compare')}
              </button>
            </div>
          )}
        </div>

        {/* Propose-AI panel (hidden in compare mode) */}
        {!compareMode && proposeState.open && (
          <div className="rounded-md border border-border bg-white p-5">
            <h3 className="text-sm font-semibold text-ink-700">{t('forecast.coach')}</h3>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs text-muted-foreground mb-1">{t('forecast.hist_start')}</span>
                <input
                  type="date"
                  value={proposeState.periodStart}
                  onChange={(e) => setProposeState((s) => ({ ...s, periodStart: e.target.value }))}
                  className="num w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-muted-foreground mb-1">{t('forecast.hist_end')}</span>
                <input
                  type="date"
                  value={proposeState.periodEnd}
                  onChange={(e) => setProposeState((s) => ({ ...s, periodEnd: e.target.value }))}
                  className="num w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block md:col-span-3">
                <span className="block text-xs text-muted-foreground mb-1">{t('forecast.context')}</span>
                <textarea
                  rows={2}
                  value={proposeState.contextNote}
                  onChange={(e) => setProposeState((s) => ({ ...s, contextNote: e.target.value }))}
                  placeholder={t('forecast.context_placeholder')}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"
                />
              </label>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => propose.mutate()}
                disabled={propose.isPending}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition"
              >
                {propose.isPending ? t('forecast.asking') : t('forecast.propose')}
              </button>
              <button
                onClick={() => setProposeState((s) => ({ ...s, open: false }))}
                className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]"
              >
                {t('common.cancel')}
              </button>
            </div>

            {propose.data && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                <div className="text-xs text-emerald-800 font-semibold mb-2">
                  ✦ {t('variance.ai_byline')}
                  <span className="ms-2 text-muted-foreground font-normal">
                    · {t('variance.cites_n', { count: propose.data.citedFactIds?.length ?? 0 })}
                  </span>
                </div>
                <DriversTable drivers={propose.data.suggestions} t={t} />
                <button
                  onClick={() => create.mutate({
                    scenarioLabel: `Forecast ${new Date().toISOString().slice(0, 10)}`,
                    drivers: propose.data!.suggestions,
                    aiSuggestions: propose.data!.suggestions,
                  })}
                  disabled={create.isPending || propose.data.suggestions.length === 0}
                  className="mt-3 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition"
                >
                  {create.isPending ? t('forecast.saving') : t('forecast.save_scenario')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Blank create panel (hidden in compare mode) */}
        {!compareMode && blank.open && (
          <div className="rounded-md border border-border bg-white p-5">
            <h3 className="text-sm font-semibold text-ink-700">{t('forecast.new_blank')}</h3>
            <label className="block mt-4">
              <span className="block text-xs text-muted-foreground mb-1">{t('forecast.scenario_label')}</span>
              <input
                value={blank.label}
                onChange={(e) => setBlank((s) => ({ ...s, label: e.target.value }))}
                placeholder={t('forecast.scenario_label_placeholder')}
                className="w-full max-w-md rounded-lg border border-border bg-white px-3 py-2 text-sm"
              />
            </label>
            <div className="mt-4 overflow-x-auto">
              <EditableDriversTable
                drivers={blank.drivers}
                t={t}
                onChange={(drivers) => setBlank((s) => ({ ...s, drivers }))}
              />
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => create.mutate({
                  scenarioLabel: blank.label.trim() || `Forecast ${new Date().toISOString().slice(0, 10)}`,
                  drivers: blank.drivers.filter((d) => d.name.trim()),
                })}
                disabled={create.isPending}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition"
              >
                {create.isPending ? t('forecast.saving') : t('forecast.save_scenario')}
              </button>
              <button
                onClick={() => setBlank({ open: false, label: '', drivers: [blankDriver()] })}
                className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Compare view */}
        {compareMode && (
          <CompareView forecasts={selectedForecasts} t={t} />
        )}

        {/* List view */}
        {!compareMode && (
          isLoading ? (
            <div className="text-sm text-muted-foreground">{t('forecast.loading')}</div>
          ) : forecasts.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-border bg-white p-12 text-center">
              <p className="text-ink-700 font-medium">{t('forecast.no_scenarios')}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t('forecast.no_scenarios_sub')}</p>
            </div>
          ) : (
            <>
              {forecasts.length >= 2 && (
                <div className="rounded-xl border border-border bg-ink-50/50 p-3 flex flex-wrap items-center gap-3">
                  <span className="text-xs text-ink-700 font-medium">{t('forecast.compare_mode')}.</span>
                  <span className="text-[11px] text-muted-foreground">{t('forecast.compare_hint')}</span>
                  {selecting.size >= 2 && (
                    <button
                      onClick={applyCompare}
                      className="ml-auto px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                    >
                      {t('forecast.compare_mode')} ({selecting.size})
                    </button>
                  )}
                </div>
              )}
              <div className="space-y-4">
                {forecasts.map((f) => (
                  <ForecastCard
                    key={f.id}
                    forecast={f}
                    wsId={wsId!}
                    t={t}
                    selectable={forecasts.length >= 2}
                    selected={selecting.has(f.id)}
                    onToggleSelect={() => toggleSelect(f.id)}
                  />
                ))}
              </div>
            </>
          )
        )}
      </main>
    </AppShell>
  );
}

function ForecastCard({
  forecast,
  wsId,
  t,
  selectable,
  selected,
  onToggleSelect,
}: {
  forecast: Forecast;
  wsId: string;
  t: TFn;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Driver[]>(forecast.drivers);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [whatIf, setWhatIf] = useState(false);
  const [rollMonths, setRollMonths] = useState<number>(forecast.rollingHorizonMonths ?? 12);

  const setRolling = useMutation({
    mutationFn: (enabled: boolean) => forecastsApi.setRolling(forecast.id, { enabled, horizonMonths: rollMonths }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecasts', wsId] }),
  });

  const save = useMutation({
    mutationFn: () => forecastsApi.update(forecast.id, draft.filter((d) => d.name.trim())),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forecasts', wsId] });
      setEditing(false);
    },
  });

  const remove = useMutation({
    mutationFn: () => forecastsApi.remove(forecast.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecasts', wsId] }),
  });

  function startEdit() {
    setDraft(forecast.drivers.length ? forecast.drivers : [blankDriver()]);
    setEditing(true);
  }

  return (
    <>
    <div className={
      'rounded-md border bg-white p-5 ' +
      (selected ? 'border-emerald-600 ring-1 ring-emerald-600/30' : 'border-border')
    }>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{forecast.scenarioLabel}</h3>
          <p className="text-xs text-muted-foreground num">
            {t('forecast.created_at', { when: new Date(forecast.createdAt).toLocaleString(), count: forecast.drivers.length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!editing && (
            <>
              <button
                onClick={startEdit}
                className="text-[11px] text-ink-600 hover:text-foreground inline-flex items-center gap-1"
              >
                <Pencil className="h-3.5 w-3.5" /> {t('common.edit')}
              </button>
              <button
                onClick={() => setWhatIf((v) => !v)}
                className={'text-[11px] inline-flex items-center gap-1 font-semibold ' + (whatIf ? 'text-emerald-800' : 'text-emerald-700 hover:text-emerald-800')}
              >
                <TrendingUp className="h-3.5 w-3.5" /> {t('whatif.button', { defaultValue: 'What-if' })}
              </button>
              <Link
                to={`/app/workspace/${wsId}/planning?forecast=${forecast.id}`}
                className="text-[11px] text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 font-semibold"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" /> {t('forecast.plan_compute')}
              </Link>
              <Link
                to={`/app/workspace/${wsId}/approvals?targetKind=forecast&targetId=${forecast.id}`}
                className="text-[11px] text-ink-600 hover:text-foreground inline-flex items-center gap-1"
                title={t('approvals.send_for_approval', { defaultValue: 'Send for approval' })}
              >
                <ClipboardCheck className="h-3.5 w-3.5" /> {t('approvals.send_for_approval', { defaultValue: 'Send for approval' })}
              </Link>
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={remove.isPending}
                className="text-[11px] text-ink-500 hover:text-warn-600 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
              </button>
            </>
          )}
          {selectable && !editing && (
            <label className="inline-flex items-center gap-1.5 text-[11px] text-ink-600 cursor-pointer">
              <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                className="accent-emerald-600"
              />
              {t('forecast.select_for_compare')}
            </label>
          )}
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        {editing ? (
          <>
            <EditableDriversTable drivers={draft} t={t} onChange={setDraft} />
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => save.mutate()}
                disabled={save.isPending}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition"
              >
                {save.isPending ? t('forecast.saving') : t('common.save')}
              </button>
              <button
                onClick={() => { setEditing(false); setDraft(forecast.drivers); }}
                className="px-4 py-2 rounded-lg border border-border text-sm text-ink-700 hover:bg-foreground/[0.04]"
              >
                {t('common.cancel')}
              </button>
              {save.isError && <span className="self-center text-xs text-warn-700">{(save.error as Error).message}</span>}
            </div>
          </>
        ) : (
          <DriversTable drivers={forecast.drivers} t={t as (k: string) => string} />
        )}
      </div>
      {whatIf && !editing && <WhatIfPanel forecast={forecast} />}
      {!editing && (
        <div className="mt-3 pt-3 border-t border-ink-100 flex flex-wrap items-center gap-2.5 text-[12px]">
          <label className="inline-flex items-center gap-1.5 text-ink-700 cursor-pointer">
            <input
              type="checkbox"
              checked={!!forecast.rollingEnabled}
              onChange={(e) => setRolling.mutate(e.target.checked)}
              disabled={setRolling.isPending}
              className="accent-emerald-600"
            />
            {t('rolling.label', { defaultValue: 'Rolling forecast' })}
          </label>
          <span className="text-muted-foreground inline-flex items-center gap-1">
            {t('rolling.keep', { defaultValue: 'keep' })}
            <input
              type="number" min={1} max={60} value={rollMonths}
              onChange={(e) => setRollMonths(Math.max(1, Math.min(60, Number(e.target.value) || 12)))}
              onBlur={() => { if (forecast.rollingEnabled) setRolling.mutate(true); }}
              className="num w-14 rounded border border-border px-1.5 py-0.5 text-right"
            />
            {t('rolling.months_ahead', { defaultValue: 'months ahead of the latest actual' })}
          </span>
          {forecast.rollingEnabled && (
            <span className="text-[11px] text-emerald-700">{t('rolling.on_hint', { defaultValue: '· auto-extends daily' })}</span>
          )}
        </div>
      )}
    </div>
    <ConfirmModal
      open={confirmDelete}
      title={t('common.delete')}
      body={t('forecast.delete_confirm', { defaultValue: 'Delete this forecast scenario? Projected facts already written are kept.' })}
      confirmLabel={t('common.delete')}
      destructive
      onConfirm={() => { remove.mutate(); setConfirmDelete(false); }}
      onCancel={() => setConfirmDelete(false)}
    />
    </>
  );
}

/** Inline-editable driver grid used by blank-create and per-card edit. */
function EditableDriversTable({
  drivers,
  t,
  onChange,
}: {
  drivers: Driver[];
  t: TFn;
  onChange: (drivers: Driver[]) => void;
}) {
  function update(i: number, patch: Partial<Driver>) {
    onChange(drivers.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }
  function remove(i: number) {
    onChange(drivers.filter((_, idx) => idx !== i));
  }
  return (
    <div>
      <table className="w-full text-sm min-w-[680px]">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-ink-100">
            <th className="py-2 pr-3 font-medium">{t('forecast.driver')}</th>
            <th className="py-2 px-3 font-medium">{t('forecast.basis')}</th>
            <th className="py-2 px-3 font-medium text-right">{t('forecast.value')}</th>
            <th className="py-2 px-3 font-medium">{t('forecast.rationale')}</th>
            <th className="py-2 pl-3 w-8" />
          </tr>
        </thead>
        <tbody>
          {drivers.map((d, i) => (
            <tr key={i} className="border-b border-ink-50 align-top">
              <td className="py-1.5 pr-3">
                <input
                  value={d.name}
                  onChange={(e) => update(i, { name: e.target.value })}
                  placeholder={t('forecast.driver')}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
                />
              </td>
              <td className="py-1.5 px-3">
                <select
                  value={d.basis}
                  onChange={(e) => update(i, { basis: e.target.value as Driver['basis'] })}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
                >
                  {BASES.map((b) => (
                    <option key={b} value={b}>{t(`forecast.basis_${b}`)}</option>
                  ))}
                </select>
              </td>
              <td className="py-1.5 px-3">
                <input
                  type="number"
                  value={d.value}
                  onChange={(e) => update(i, { value: Number(e.target.value) })}
                  className="num w-28 rounded-md border border-border bg-white px-2 py-1.5 text-sm text-right tabular-nums"
                />
              </td>
              <td className="py-1.5 px-3">
                <input
                  value={d.rationale}
                  onChange={(e) => update(i, { rationale: e.target.value })}
                  placeholder={t('forecast.rationale')}
                  className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm"
                />
              </td>
              <td className="py-1.5 pl-3 text-right">
                <button
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-warn-700 p-1"
                  aria-label={t('common.delete')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        onClick={() => onChange([...drivers, blankDriver()])}
        className="mt-2 text-xs text-emerald-700 hover:text-emerald-800 font-semibold inline-flex items-center gap-1"
      >
        <Plus className="h-3.5 w-3.5" /> {t('forecast.add_driver')}
      </button>
    </div>
  );
}

function DriversTable({ drivers, t }: { drivers: Driver[]; t: (k: string) => string }) {
  if (drivers.length === 0) return <p className="text-sm text-muted-foreground">{t('forecast.no_drivers')}</p>;
  return (
    <table className="w-full text-sm min-w-[640px]">
      <thead>
        <tr className="text-left text-muted-foreground border-b border-ink-100">
          <th className="py-2 pr-3 font-medium">{t('forecast.driver')}</th>
          <th className="py-2 px-3 font-medium">{t('forecast.basis')}</th>
          <th className="py-2 px-3 font-medium text-right">{t('forecast.value')}</th>
          <th className="py-2 px-3 font-medium">{t('forecast.rationale')}</th>
        </tr>
      </thead>
      <tbody>
        {drivers.map((d, i) => (
          <tr key={i} className="border-b border-ink-50">
            <td className="py-2 pr-3 font-medium text-foreground">{d.name}{d.flagged && <span className="ml-2 text-xs text-warn-600">{t('forecast.flagged')}</span>}</td>
            <td className="py-2 px-3 text-ink-700">{d.basis}</td>
            <td className="py-2 px-3 num text-right font-medium text-foreground tabular-nums">{Number(d.value).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
            <td className="py-2 px-3 text-ink-600 text-xs">{d.rationale}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Side-by-side driver compare: rows are the union of driver names across
 * the selected scenarios, columns are one per scenario. Missing values
 * render as "—" so the gap between scenarios is obvious.
 */
function CompareView({ forecasts, t }: { forecasts: Forecast[]; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const driverNames = useMemo(() => {
    const names = new Set<string>();
    forecasts.forEach((f) => f.drivers.forEach((d) => names.add(d.name)));
    return Array.from(names).sort();
  }, [forecasts]);

  if (forecasts.length < 2) {
    return (
      <div className="rounded-md border-2 border-dashed border-border bg-white p-12 text-center">
        <p className="text-ink-700 font-medium">{t('forecast.compare_hint')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-white p-5 overflow-x-auto">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="text-left text-muted-foreground border-b border-ink-100">
            <th className="py-2 pr-3 font-medium text-[11px] uppercase tracking-[0.14em]">
              {t('forecast.compare_col_driver')}
            </th>
            {forecasts.map((f) => (
              <th key={f.id} className="py-2 px-3 font-medium text-right text-foreground">
                {f.scenarioLabel}
                <div className="text-[10px] text-muted-foreground num font-normal">
                  {new Date(f.createdAt).toISOString().slice(0, 10)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {driverNames.map((name) => (
            <tr key={name} className="border-b border-ink-50 align-top">
              <td className="py-2 pr-3 font-medium text-foreground">{name}</td>
              {forecasts.map((f) => {
                const d = f.drivers.find((x) => x.name === name);
                return (
                  <td key={f.id} className="py-2 px-3 num text-right text-foreground tabular-nums">
                    {d ? Number(d.value).toLocaleString(undefined, { maximumFractionDigits: 4 }) : <span className="text-ink-400">—</span>}
                    {d?.basis && (
                      <div className="text-[10px] text-muted-foreground font-normal">{d.basis}</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
